import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

interface ConfirmOptions {
  message: string;
  title?: string;
  okLabel?: string;
  cancelLabel?: string;
  danger?: boolean; // червона кнопка ОК (для видалення)
}

type ConfirmFn = (opts: ConfirmOptions | string) => Promise<boolean>;

const ConfirmCtx = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Fallback на window.confirm, якщо провайдер не змонтовано
    return (opts) => Promise.resolve(window.confirm(typeof opts === 'string' ? opts : opts.message));
  }
  return ctx;
}

interface State extends ConfirmOptions {
  open: boolean;
  resolve: ((v: boolean) => void) | null;
}

const EMPTY: State = { open: false, message: '', resolve: null };

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>(EMPTY);

  const confirm = useCallback<ConfirmFn>((opts) => {
    const normalized: ConfirmOptions = typeof opts === 'string' ? { message: opts } : opts;
    return new Promise<boolean>((resolve) => {
      setState({ ...EMPTY, ...normalized, open: true, resolve });
    });
  }, []);

  const close = useCallback((value: boolean) => {
    setState((s) => {
      s.resolve?.(value);
      return EMPTY;
    });
  }, []);

  // ESC закриває (= Cancel), Enter підтверджує (= OK).
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(false); }
      else if (e.key === 'Enter') { e.preventDefault(); close(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.open, close]);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state.open && (
        <div
          role="presentation"
          onClick={() => close(false)}
          style={{
            position: 'fixed', inset: 0, background: 'var(--overlay)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-panel)', borderRadius: 8, padding: '1.25rem 1.5rem',
              minWidth: 320, maxWidth: 480, boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
            }}
          >
            <h3 id="confirm-title" style={{ margin: '0 0 0.5rem', fontSize: '1.05rem' }}>
              {state.title ?? 'Підтвердження'}
            </h3>
            <p style={{ margin: '0 0 1.25rem', color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>
              {state.message}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button onClick={() => close(false)} autoFocus>
                {state.cancelLabel ?? 'Скасувати'}
              </button>
              <button
                onClick={() => close(true)}
                className={state.danger ? 'danger' : 'primary'}
              >
                {state.okLabel ?? 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}
